import {
    Box,
    Button,
    FormControl,
    InputLabel,
    NativeSelect,
    Typography,
    useTheme
} from "@mui/material"
import useMediaQuery from "@mui/material/useMediaQuery"
import * as yup from "yup"

import { DataGrid, GridDeleteIcon } from "@mui/x-data-grid"
import { useFormik } from "formik"
import { useEffect, useState } from "react"
import ConfirmationDialog from "../../components/ConfirmationDialog"
import Header from "../../components/Header"
import ProjectPermissionApi from "../../services/api/ProjectPermission"
import ProjectApi from "../../services/api/projects"
import RoleApi from "../../services/api/roles"
import { tokens } from "../../theme"

const ProjectPermissions = () => {
	const isNonMobile = useMediaQuery("(min-width:600px)")
	const theme = useTheme()
	const colors = tokens(theme.palette.mode)
	const [isEdit, setIsEdit] = useState(false)
	const [projects, setProjects] = useState([])
	const [roles, setRoles] = useState([])
	const [roleId, setRoleId] = useState()
	const [roleProjects, setRoleProjects] = useState([])

	const columns = [
		{
			field: "projectName",
			headerName: "Project",
			flex: 1,
			cellClassName: "name-column--cell",
		},
		{
			field: "",
			headerName: "Action",
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Box display="flex" justifyContent="center" borderRadius="4px">
						<Typography
							variant="button"
							color="error"
							onClick={() => destroy(row)}
							style={{ cursor: "pointer" }}
						>
							<GridDeleteIcon fontSize="small" style={{ marginRight: "4px" }} />
							Delete
						</Typography>
					</Box>
				)
			},
		},
	]

	useEffect(() => {
		fetchRoles()
		fetchProjects()
	}, [])

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: checkoutSchema,

		onSubmit: async (values, { resetForm }) => {
			createEntity(values)

			resetForm({ values: { ...initialValues, role_id: roleId } })
			handleAfterReset()
		},
	})

	const createEntity = async (values) => {
		const response = await ProjectPermissionApi().create(values)

		console.log(createdSuccessMsg, response)
		fetchRoleProjects(roleId)
	}

	const handleAfterReset = () => {
		setIsEdit(false)
	}

	const fetchRoles = async () => {
		try {
			const data = await RoleApi().getAll()

			setRoles(data.results)
		} catch (error) {
			console.log("Not loading roles", error)
		}
	}

	const fetchProjects = async () => {
		try {
			const data = await ProjectApi().getAll()

			setProjects(data.results)
		} catch (error) {
			console.log("Not loading projects", error)
		}
	}

	const handleRoleChange = (event) => {
		setRoleId(event.target.value)
		fetchRoleProjects(event.target.value)
		formik.handleChange(event)
	}

	const [isConfirmationOpen, setConfirmationOpen] = useState(false)
	const [selectedId, setSelectedId] = useState("")

	const destroy = (row) => {
		setSelectedId(row.id)
		handleOpenConfirmation()
	}

	const handleOpenConfirmation = () => {
		setConfirmationOpen(true)
	}

	const handleCloseConfirmation = () => {
		setConfirmationOpen(false)
	}

	const handleConfirmAction = () => {
		try {
			const response = ProjectPermissionApi().destroy(selectedId)

			console.log(deletedSuccessMsg, response)
			fetchRoleProjects(roleId)
		} catch (error) {
			console.log(deleteErrorMsg, error)
		}

		handleCloseConfirmation()
	}

	const fetchRoleProjects = async (roleId) => {
		try {
			const data = await ProjectPermissionApi().getByRoleId(roleId)

			setRoleProjects(data)
		} catch (error) {
			setRoleProjects([])
			if (error.response.status === 404) {
				console.log("This role has no projects.")
			} else {
				console.log("Loading error", error.response)
			}
		}
	}

	return (
		<>
			<Box m="20px">
				<Header title={`${formName}`} subtitle="" />
				<form onSubmit={formik.handleSubmit}>
					<Box
						display="grid"
						gap="30px"
						gridTemplateColumns="repeat(4, minmax(0, 1fr))"
						sx={{
							"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
						}}
					>
						<Box sx={{ gridColumn: "span 2" }}>
							<FormControl fullWidth>
								<InputLabel variant="standard" htmlFor="uncontrolled-native">
									Role
								</InputLabel>
								<NativeSelect
									value={formik.values.role_id}
									onChange={handleRoleChange}
									inputProps={{
										name: "role_id",
									}}
								>
									<option value=""></option>
									{roles
										? roles.map((role) => (
												<option value={role.id}>{role.name}</option>
										  ))
										: null}
								</NativeSelect>
							</FormControl>

							<Box
								m="20px 0 0 0"
								display="grid"
								gap="30px"
								gridTemplateColumns="repeat(4, minmax(0, 1fr))"
								sx={{
									"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
								}}
							>
								<FormControl
									fullWidth
									sx={{ gridColumn: "span 2", marginTop: "8px" }}
								>
									<InputLabel variant="standard" htmlFor="uncontrolled-native">
										Project
									</InputLabel>
									<NativeSelect
										value={formik.values.project_id}
										onChange={formik.handleChange}
										inputProps={{
											name: "project_id",
										}}
									>
										<option value=""></option>
										{projects
											? projects.map((project) => (
													<option value={project.id}>{project.name}</option>
											  ))
											: null}
									</NativeSelect>
								</FormControl>
							</Box>
							<Box display="flex" justifyContent="end" mt="20px">
								<Button type="submit" color="secondary" variant="contained">
									{isEdit ? "Update" : "Save"}
								</Button>
							</Box>
						</Box>

						<Box sx={{ gridColumn: "span 2" }}>
							<Box
								height="75vh"
								sx={{
									"& .MuiDataGrid-root": {
										border: "none",
									},
									"& .MuiDataGrid-cell": {
										borderBottom: "none",
									},
									"& .name-column--cell": {
										color: colors.greenAccent[300],
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: colors.blueAccent[700],
										borderBottom: "none",
									},
									"& .MuiDataGrid-virtualScroller": {
										backgroundColor: colors.primary[400],
									},
									"& .MuiDataGrid-footerContainer": {
										borderTop: "none",
										backgroundColor: colors.blueAccent[700],
									},
									"& .MuiCheckbox-root": {
										color: `${colors.greenAccent[200]} !important`,
									},
								}}
							>
								{roleProjects && (
									<DataGrid rows={roleProjects} columns={columns} />
								)}
							</Box>
						</Box>
					</Box>
				</form>
			</Box>
			<ConfirmationDialog
				open={isConfirmationOpen}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
			/>
		</>
	)
}

const checkoutSchema = yup.object().shape({
	role_id: yup.number().required("required"),
	project_id: yup.number().required("required"),
})

const initialValues = {
    role_id: "",
	project_id: "",
}

const formName = "Project Permission"
const createdSuccessMsg = `${formName} created:`
const updatedSuccessMsg = `${formName} updated:`
const deletedSuccessMsg = `${formName} deleted successfully: `
const deleteErrorMsg = `Not deleted ${formName}`

export default ProjectPermissions
